import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, CircleAlert, LoaderCircle, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { RudderstackBrand } from '@/_shared/components/RudderstackBrand';
import { useAuth } from '@/_shared/hooks/useAuth';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { client } from '@/_shared/lib/rpc';

type SlugStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

export function NewWorkspace(): React.ReactElement {
  usePageTitle('New Workspace');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, loading: authLoading } = useAuth();

  const [slug, setSlug] = useState('');
  const [rsWorkspaceId, setRsWorkspaceId] = useState('');
  const [rsPat, setRsPat] = useState('');
  const [slugStatus, setSlugStatus] = useState<SlugStatus>('idle');
  const [slugMessage, setSlugMessage] = useState('');
  const [error, setError] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Debounced slug availability check
  const checkSlug = useCallback(async (value: string) => {
    if (value.length < 2) {
      setSlugStatus('idle');
      setSlugMessage('');
      return;
    }
    setSlugStatus('checking');
    try {
      const result = await client.workspace.checkSlug({ slug: value });
      if (result.available) {
        setSlugStatus('available');
        setSlugMessage('');
      } else {
        setSlugStatus(result.reason?.includes('taken') ? 'taken' : 'invalid');
        setSlugMessage(result.reason ?? 'Unavailable');
      }
    } catch {
      setSlugStatus('invalid');
      setSlugMessage('Could not check availability');
    }
  }, []);

  const handleSlugChange = useCallback(
    (value: string) => {
      const normalized = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
      setSlug(normalized);
      setSlugStatus('idle');
      setSlugMessage('');
      clearTimeout(debounceRef.current);
      if (normalized.length >= 2) {
        debounceRef.current = setTimeout(() => void checkSlug(normalized), 300);
      }
    },
    [checkSlug],
  );

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const createMutation = useMutation({
    mutationFn: async () => {
      return client.workspace.create({
        slug,
        rudderstackWorkspaceId: rsWorkspaceId,
        rudderstackPat: rsPat,
      });
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries();
      void navigate(`/w/${data.workspace.id}`, { replace: true });
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const canSubmit =
    slug.length >= 2 &&
    slugStatus === 'available' &&
    rsWorkspaceId.trim().length > 0 &&
    rsPat.trim().length > 0 &&
    !createMutation.isPending;

  // If not logged in, redirect to login
  if (!authLoading && !user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top_left,rgba(16,94,213,0.1),transparent_26%),linear-gradient(180deg,#f8fbff_0%,#f4f7fc_100%)] px-4">
      <div className="w-full max-w-md animate-fade-in rounded-[1.75rem] border border-border/80 bg-white/92 p-8 shadow-[0_28px_60px_-38px_rgba(47,66,112,0.38)] backdrop-blur-xl">
        <RudderstackBrand className="mb-5 justify-center" label="Rudder AI" badge="beta" />
        <h1 className="mb-2 text-center text-xl font-semibold tracking-[-0.02em] text-foreground">
          Create your workspace
        </h1>
        <p className="mb-8 text-center text-sm text-muted-foreground">
          Connect your RudderStack account to get started.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) createMutation.mutate();
          }}
          className="space-y-5"
        >
          {/* Workspace slug */}
          <div>
            <label htmlFor="ws-slug" className="mb-1.5 block text-sm font-medium text-foreground">
              Workspace URL
            </label>
            <div className="flex items-center gap-0">
              <span className="flex h-10 items-center rounded-l-xl border border-r-0 border-border/80 bg-muted/30 px-3 text-xs text-muted-foreground">
                rudderai.com/w/
              </span>
              <div className="relative flex-1">
                <input
                  id="ws-slug"
                  type="text"
                  value={slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  placeholder="my-team"
                  maxLength={30}
                  autoFocus
                  className="h-10 w-full rounded-r-xl border border-border/80 bg-white px-3 pr-9 text-sm text-foreground transition-colors outline-none placeholder:text-muted-foreground/60 focus:border-primary/50 focus:ring-1 focus:ring-primary/20"
                />
                <span className="absolute top-1/2 right-2.5 -translate-y-1/2">
                  {slugStatus === 'checking' && <LoaderCircle className="size-4 animate-spin text-muted-foreground" />}
                  {slugStatus === 'available' && <Check className="size-4 text-green-600" />}
                  {(slugStatus === 'taken' || slugStatus === 'invalid') && <X className="size-4 text-red-500" />}
                </span>
              </div>
            </div>
            {slugMessage && (
              <p className="mt-1 text-xs text-red-600" role="alert">
                {slugMessage}
              </p>
            )}
            <p className="mt-1 text-xs text-muted-foreground">
              Lowercase letters, numbers, and hyphens. Cannot be changed later.
            </p>
          </div>

          {/* RudderStack Workspace ID */}
          <div>
            <label htmlFor="rs-workspace-id" className="mb-1.5 block text-sm font-medium text-foreground">
              RudderStack Workspace ID
            </label>
            <input
              id="rs-workspace-id"
              type="text"
              value={rsWorkspaceId}
              onChange={(e) => setRsWorkspaceId(e.target.value)}
              placeholder="2abc1234defg5678hij..."
              className="h-10 w-full rounded-xl border border-border/80 bg-white px-3 text-sm text-foreground transition-colors outline-none placeholder:text-muted-foreground/60 focus:border-primary/50 focus:ring-1 focus:ring-primary/20"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Find this at{' '}
              <a
                href="https://app.rudderstack.com/workspace"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                app.rudderstack.com/workspace
              </a>
            </p>
          </div>

          {/* RudderStack PAT */}
          <div>
            <label htmlFor="rs-pat" className="mb-1.5 block text-sm font-medium text-foreground">
              RudderStack Personal Access Token
            </label>
            <input
              id="rs-pat"
              type="password"
              value={rsPat}
              onChange={(e) => setRsPat(e.target.value)}
              placeholder="Enter your PAT..."
              className="h-10 w-full rounded-xl border border-border/80 bg-white px-3 text-sm text-foreground transition-colors outline-none placeholder:text-muted-foreground/60 focus:border-primary/50 focus:ring-1 focus:ring-primary/20"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Generate at{' '}
              <a
                href="https://app.rudderstack.com/profile"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                app.rudderstack.com/profile
              </a>
              . Stored encrypted.
            </p>
          </div>

          {error && (
            <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <CircleAlert className="size-4 shrink-0" />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-primary text-sm font-semibold text-primary-foreground shadow-[0_22px_40px_-28px_rgba(16,94,213,0.9)] transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {createMutation.isPending ? <LoaderCircle className="size-4 animate-spin" /> : 'Create Workspace'}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          <button
            type="button"
            onClick={async () => {
              await fetch('/auth/logout', { method: 'POST', credentials: 'include' });
              window.location.href = '/login';
            }}
            className="hover:text-foreground hover:underline"
          >
            Sign in with a different account
          </button>
        </p>
      </div>
    </div>
  );
}
